import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, takeUntil, tap } from "rxjs/operators";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonService } from "../../../services/common.service";
import { ApiService } from "../../../services/api.service";
import {
  PERMISSIONS,
  RESOURCE_ACTION,
  ReviewStatus,
  STATIC_KEYS,
  USER_ROLE,
} from "src/constants/constants";
import { ConfirmationDialogComponent } from "src/pages/shared/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ErrorDialogComponent } from "src/pages/shared/error-dialog/error-dialog.component";
import { RemarkModalComponent } from "src/pages/shared/remark-modal/remark-modal.component";
import { ReasonDetailModalComponent } from "src/pages/shared/reason-detail-modal/reason-detail-modal.component";
import { AlertService } from "src/services/alert.service";
import { formatDate } from "src/helper";
import { FilterDialogComponent } from "src/components/filter-dialog/filter-dialog.component";
import { ArtefactFilterOptions } from "src/pages/manage-grievances/constants";
import { UtilityService } from "src/services/utility.service";

@Component({
  selector: "app-user-list",
  templateUrl: "./user-list.component.html",
  styleUrls: ["./user-list.component.scss"],
})
export class UserListComponent implements OnInit, OnDestroy {
  limit = 20;
  currentPage = 0;
  currentTap: string = USER_ROLE.ORG_ADMIN;
  selectedTabIndex = 0;
  indexTab: Record<number, string> = {
    0: USER_ROLE.ORG_ADMIN,
    1: USER_ROLE.CONTRIBUTOR,
    2: USER_ROLE.EXPLORER,
  };
  tabIndex: Record<string, number> = {
    [USER_ROLE.ORG_ADMIN]: 0,
    [USER_ROLE.CONTRIBUTOR]: 1,
    [USER_ROLE.EXPLORER]: 2,
  };
  userList: any[] = [];
  userCount = 0;
  searchValue = "";
  searchQuery = "";
  fetching = false;
  permissions = PERMISSIONS;

  private destroy$ = new Subject<void>();
  keyword = new Subject<string>();

  columns = [
    {
      name: "User name",
      key: "name",
      width: "233px",
      type: "text",
      avatarKey: "profile_image",
      // iconUrl: "assets/images/info-line.svg",
    },
    {
      name: "ORG NAME",
      key: "orgName",
      width: "233px",
      type: "text",
      // iconUrl: "assets/images/info-line.svg",
    },
    {
      name: "ORG. DESIGNATION",
      key: "designation",
      width: "233px",
      type: "text",
    },
    { name: "Join Date", key: "createdAt", width: "233px", type: "text" },
    { name: "Status", key: "reviewStatus", width: "115px", type: "text" },
  ];

  tableColumns: any = [];
  menuOptions = [
    {
      key: "Block Access",
      event: "blockAccess",
      style: { color: "#E24C3F" },
      iconPath: "assets/images/blockIconMenu.svg",
      permission: RESOURCE_ACTION.USER_BLOCK,
    },
    // {
    //   key: "Delete",
    //   event: "delete",
    //   style: { color: "#E24C3F" },
    //   iconPath: "assets/images/deleteIconMenu.svg",
    // },
  ];

  dataSource: MatTableDataSource<any> = new MatTableDataSource();

  requiredFilterConfig = {
    uploadedOn: true,
    userStatus: true,
  };
  public appliedFilters?: ArtefactFilterOptions;
  public filtersToApply = {};

  constructor(
    private router: Router,
    private commonService: CommonService,
    private apiService: ApiService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private utilityService: UtilityService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.keyword
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.currentPage = 0;
        this.limit = 20;
        this.filtersToApply = {
          ...this.filtersToApply,
          search: searchTerm?.trim() || "",
        };
        this.getAdminUserList();
      });
    this.route.queryParams.subscribe((params) => {
      this.currentTap = params["currentTab"] || USER_ROLE.ORG_ADMIN;
      this.tableColumns = this.columns;
      this.setFilterValue();
      this.getAdminUserList();
    });
  }

  setFilterValue() {
    const raw = this.commonService.getItem(STATIC_KEYS.USER_LIST, true);
    const data = raw ? JSON.parse(raw) : {};
    this.selectedTabIndex = this.tabIndex[this.currentTap];

    this.appliedFilters = data?.appliedFilters || {};
    this.filtersToApply = data?.filtersToApply || {};
    if (this.currentTap === USER_ROLE.EXPLORER) {
      this.tableColumns = this.columns.filter(
        ({ key }) => key !== "designation"
      );
    }
  }

  setPersistFilter() {
    this.commonService.setItem(
      STATIC_KEYS.USER_LIST,
      JSON.stringify({
        appliedFilters: this.appliedFilters,
        filtersToApply: this.filtersToApply,
      }),
      true
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchHandler(event: any) {
    this.searchValue = event.target.value;
    this.keyword.next(this.searchValue);
  }

  pageChanged(event: any) {
    this.limit = event.pageSize;
    this.currentPage = event.pageIndex;
    this.getAdminUserList();
  }

  onTabChange(event: any) {
    this.currentPage = 0;
    this.limit = 20;
    this.searchValue = "";
    this.filtersToApply = {};
    this.currentTap = this.indexTab[event.index] || USER_ROLE.ORG_ADMIN;
    this.appliedFilters = undefined;
    this.router.navigate(["/admin/user/list"], {
      queryParams: { currentTab: this.currentTap },
    });
  }

  handleAction(event: { action: string; item: any }) {
    const {
      action,
      item: { id },
    } = event;

    if (action === "delete") {
      this.openConfirmationDialog(id);
    } else if (action === "blockAccess") {
      this.openReviewModalDialog(id);
    } else if (action === "unBlockUser") {
      this.handleUnblockUser(id);
    } else if (action === "viewBlockAccess") {
      this.handleViewBlockReason(id);
    }
  }

  handleUnblockUser(userId: string) {
    this.apiService
      .blockUnblockUserAccessList({}, userId, "unblock")
      .subscribe((res) => {
        this.alertService.showSnackBar(res?.data?.message || res?.header?.msg);
        this.getAdminUserList();
      });
  }

  handleViewBlockReason(userId: string) {
    this.apiService.getBlockUserReason(userId).subscribe({
      next: (res) => {
        if (res.header.error) return;
        this.dialog.open(ReasonDetailModalComponent, {
          width: "469px",
          data: {
            reason: res.data.reason,
            heading: "User Blocked",
            subHeading: "This is the reason provided by you",
          },
        });
      },
    });
  }

  customMenuOptions(user: any) {
    if (!user?.isActive && !user?.isBlocked) {
      return [];
    }
    if (user?.isBlocked) {
      return [
        {
          key: "View Block Reason",
          event: "viewBlockAccess",
          iconPath: "assets/images/eyeView.svg",
        },
        {
          key: "Unblock User",
          event: "unBlockUser",
          permission: RESOURCE_ACTION.USER_UNBLOCK,
          iconPath: "assets/images/unblockIconMenu.svg",
        },
      ];
    }

    return this.menuOptions;
  }

  openConfirmationDialog(id: string) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: "Are you sure you want to delete this user?",
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.apiService
          .deleteUserByID(id)
          .pipe(
            tap({
              error: (err) =>
                this.dialog.open(ErrorDialogComponent, {
                  data: err?.message,
                }),
            })
          )
          .subscribe(() => this.getAdminUserList());
      }
    });
  }

  openReviewModalDialog(id: string) {
    const dialogRef = this.dialog.open(RemarkModalComponent, {
      width: "459px",
      data: {
        required: true,
        heading: "Block Access",
      },
    });

    dialogRef.afterClosed().subscribe((reason) => {
      if (reason) {
        this.apiService
          .blockUnblockUserAccessList({ reason }, id, "block")
          .pipe(
            tap({
              error: (err) =>
                this.dialog.open(ErrorDialogComponent, {
                  data: err?.message,
                }),
            })
          )
          .subscribe((res) => {
            this.alertService.showSnackBar(
              res?.data?.message || res?.header?.msg
            );
            this.getAdminUserList();
          });
      }
    });
  }

  getAdminUserList() {
    const params = {
      page: this.currentPage + 1,
      size: this.limit,
      filter: {
        ...this.filterAddons(),
        roles: [this.currentTap],
      },
    };
    this.setPersistFilter();
    this.apiService
      .getUserList(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.userList = this.formatUserList(res?.data?.list || []);
        this.userCount = res?.data?.total || 0;
      });
  }

  formatUserList(list: any[]): any[] {
    return list.map((item) => ({
      ...item,
      reviewStatus: item?.isBlocked
        ? ReviewStatus.Blocked
        : item?.isActive
        ? ReviewStatus.Active
        : ReviewStatus.Inactive,
      createdAt: formatDate(item?.createdAt) ?? "",
      orgName: item?.organisation?.name ?? "-",
    }));
  }

  onFilterClick() {
    this.dialog
      .open(FilterDialogComponent, {
        data: {
          filters: this.appliedFilters,
          config: this.requiredFilterConfig,
        },
      })
      .afterClosed()
      .subscribe((filters?: ArtefactFilterOptions) => {
        if (!filters) {
          return;
        }

        this.appliedFilters = filters;

        this.filtersToApply = {
          ...this.filtersToApply,
          joiningStart: this.utilityService.formatDate(
            filters?.actionDate?.fromDate
          ),
          joiningEnd: this.utilityService.formatDate(
            filters?.actionDate?.toDate
          ),
          status: filters?.userStatusType?.value,
        };
        this.pageChanged({ pageIndex: 0, pageSize: 20 });
      });
  }

  filterAddons() {
    // this.setPersistFilter();

    return this.utilityService.removeBlankFields<
      ArtefactFilterOptions,
      ArtefactFilterOptions
    >(this.filtersToApply);
  }

  onRemoveFilter(data: ArtefactFilterOptions) {
    this.appliedFilters = data;

    this.filtersToApply = {
      ...this.filtersToApply,
      joiningStart: this.utilityService.formatDate(data?.actionDate?.fromDate),
      joiningEnd: this.utilityService.formatDate(data?.actionDate?.toDate),
      status: data?.userStatusType?.value,
    };

    this.pageChanged({ pageIndex: 0, pageSize: 20 });
  }

  onFilterChange(data: any) {
    this.filtersToApply = {
      ...this.filtersToApply,
      joiningStart: this.utilityService.formatDate(data?.actionDate?.fromDate),
      joiningEnd: this.utilityService.formatDate(data?.actionDate?.toDate),
      status: data?.userStatusType?.value,
    };
    this.pageChanged({ pageIndex: 0, pageSize: 20 });
  }
}
